import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CreditCard,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { cardPrototypeAPI } from '../services/api';

interface CardPrototype {
  id: number;
  name: string;
  cardType: string;
  cardNature: string;
  validDays: number;
  featureType?: string;
  courseLimitScope?: string;
  price?: number | null;
  description?: string;
  active: boolean;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface CardPrototypeForm {
  name: string;
  cardType: string;
  cardNature: string;
  validDays: number | '';
  featureType: string;
  courseLimitScope: string;
  price: number | '' | null;
  description: string;
  active: boolean;
  sortOrder: number | '' | null;
}

type CardNature = '正式' | '试用';
type CardFeatureType = 'TIME_LIMIT' | 'COURSE_LIMIT';
type CourseLimitScope = 'SINGLE_GRADE' | 'STAGE_ONLY';

const FEATURE_TYPE_LABEL: Record<CardFeatureType, string> = {
  TIME_LIMIT: '时效限制',
  COURSE_LIMIT: '课程限制',
};

const normalizeCardNature = (value: any): CardNature | null => {
  const v = String(value ?? '').trim();
  if (v === '正式' || v === '试用') return v;
  return null;
};

const normalizeFeatureType = (value: any): CardFeatureType => {
  const v = String(value ?? '').trim().toUpperCase();
  return v === 'COURSE_LIMIT' ? 'COURSE_LIMIT' : 'TIME_LIMIT';
};

const normalizeCourseLimitScope = (value: any): CourseLimitScope | null => {
  const v = String(value ?? '').trim().toUpperCase();
  if (v === 'SINGLE_GRADE') return 'SINGLE_GRADE';
  if (v === 'STAGE_ONLY') return 'STAGE_ONLY';
  return null;
};

const buildAllowedValidDays = (nature: CardNature | null): number[] => {
  if (nature === '试用') return [1];
  if (nature === '正式') return [180, 365];
  return [1, 180, 365];
};

const toNumberOrEmpty = (value: any): number | '' => {
  if (value === '' || value === null || value === undefined) return '';
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return num;
};

const CardPrototypeManagePage: React.FC = () => {
  const [items, setItems] = useState<CardPrototype[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CardPrototype | null>(null);
  const [deleting, setDeleting] = useState<CardPrototype | null>(null);

  const [form, setForm] = useState<CardPrototypeForm>({
    name: '',
    cardType: '',
    cardNature: '正式',
    validDays: 180,
    featureType: 'TIME_LIMIT',
    courseLimitScope: 'SINGLE_GRADE',
    price: '',
    description: '',
    active: true,
    sortOrder: 0,
  });

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cardPrototypeAPI.getAll();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('加载原型卡失败');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aOrder = a.sortOrder ?? 0;
      const bOrder = b.sortOrder ?? 0;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (b.id ?? 0) - (a.id ?? 0);
    });
  }, [items]);

  const allowedValidDays = useMemo(() => {
    const nature = normalizeCardNature(form.cardNature);
    const allowed = buildAllowedValidDays(nature);
    const current = typeof form.validDays === 'number' ? form.validDays : Number(form.validDays);
    if (Number.isFinite(current) && !allowed.includes(current)) {
      return [current, ...allowed].filter((v, idx, arr) => arr.indexOf(v) === idx);
    }
    return allowed;
  }, [form.cardNature, form.validDays]);

  const handleInputChange = (field: keyof CardPrototypeForm, value: any) => {
    if (field === 'cardNature') {
      const nextNature = normalizeCardNature(value) || '正式';
      const allowed = buildAllowedValidDays(nextNature);
      setForm(prev => {
        const nextDays = typeof prev.validDays === 'number' ? prev.validDays : Number(prev.validDays);
        const fixedDays = Number.isFinite(nextDays) && allowed.includes(nextDays) ? prev.validDays : allowed[0];
        let nextScope = prev.courseLimitScope;
        const ft = normalizeFeatureType(prev.featureType);
        if (ft === 'COURSE_LIMIT') {
          nextScope = nextNature === '试用' ? 'SINGLE_GRADE' : (normalizeCourseLimitScope(prev.courseLimitScope) || 'SINGLE_GRADE');
        } else {
          nextScope = 'SINGLE_GRADE';
        }
        return { ...prev, cardNature: nextNature, validDays: fixedDays, courseLimitScope: nextScope };
      });
      return;
    }

    if (field === 'featureType') {
      const nextFeatureType = normalizeFeatureType(value);
      setForm(prev => {
        const nature = normalizeCardNature(prev.cardNature) || '正式';
        let nextScope = prev.courseLimitScope;
        if (nextFeatureType === 'COURSE_LIMIT') {
          nextScope = nature === '试用' ? 'SINGLE_GRADE' : (normalizeCourseLimitScope(prev.courseLimitScope) || 'SINGLE_GRADE');
        } else {
          nextScope = 'SINGLE_GRADE';
        }
        return { ...prev, featureType: nextFeatureType, courseLimitScope: nextScope };
      });
      return;
    }

    if (field === 'validDays') {
      setForm(prev => ({ ...prev, validDays: toNumberOrEmpty(value) }));
      return;
    }

    if (field === 'courseLimitScope') {
      setForm(prev => ({ ...prev, courseLimitScope: String(value) }));
      return;
    }

    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (event: SelectChangeEvent<string | number>) => {
    const { name, value } = event.target;
    handleInputChange(name as keyof CardPrototypeForm, value);
  };

  const openCreate = () => {
    setForm({
      name: '',
      cardType: '',
      cardNature: '正式',
      validDays: 180,
      featureType: 'TIME_LIMIT',
      courseLimitScope: 'SINGLE_GRADE',
      price: '',
      description: '',
      active: true,
      sortOrder: 0,
    });
    setDialogOpen(true);
  };

  const openEdit = (item: CardPrototype) => {
    setEditing(item);
    setForm({
      name: item.name || '',
      cardType: item.cardType || '',
      cardNature: normalizeCardNature(item.cardNature) || '正式',
      validDays: typeof item.validDays === 'number' ? item.validDays : 180,
      featureType: normalizeFeatureType(item.featureType),
      courseLimitScope: normalizeCourseLimitScope(item.courseLimitScope) || 'SINGLE_GRADE',
      price: item.price ?? '',
      description: item.description || '',
      active: !!item.active,
      sortOrder: item.sortOrder ?? 0,
    });
    setEditDialogOpen(true);
  };

  const openDelete = (item: CardPrototype) => {
    setDeleting(item);
    setDeleteDialogOpen(true);
  };

  const buildPayload = () => {
    const validDays = typeof form.validDays === 'number' ? form.validDays : Number(form.validDays);
    const sortOrder = form.sortOrder === '' || form.sortOrder === null ? null : Number(form.sortOrder);
    const price = form.price === '' || form.price === null ? null : Number(form.price);
    const featureType = normalizeFeatureType(form.featureType);
    const courseLimitScope = featureType === 'COURSE_LIMIT' ? (normalizeCourseLimitScope(form.courseLimitScope) || undefined) : undefined;
    return {
      name: form.name.trim(),
      cardType: form.cardType.trim(),
      cardNature: normalizeCardNature(form.cardNature) || '正式',
      validDays,
      featureType,
      courseLimitScope,
      price,
      description: form.description || undefined,
      active: form.active,
      sortOrder,
    };
  };

  const validateForm = () => {
    if (!form.name.trim()) return '原型名称必填';
    if (!form.cardType.trim()) return '卡片类型必填';

    const nature = normalizeCardNature(form.cardNature);
    if (!nature) return '卡片性质必填';

    const days = typeof form.validDays === 'number' ? form.validDays : Number(form.validDays);
    if (!Number.isFinite(days) || days <= 0) return '有效天数必须为正整数';

    if (nature === '试用' && days !== 1) return '试用卡有效天数必须为1';
    if (nature === '正式' && days !== 180 && days !== 365) return '正式卡有效天数必须为180或365';

    const ft = normalizeFeatureType(form.featureType);
    if (ft !== 'TIME_LIMIT' && ft !== 'COURSE_LIMIT') return '功能类型不合法';
    if (ft === 'COURSE_LIMIT') {
      const scope = normalizeCourseLimitScope(form.courseLimitScope);
      if (nature === '试用') {
        if (scope !== 'SINGLE_GRADE') return '课程限制型试用卡限制形态固定为仅1个年级';
      } else {
        if (!scope) return '课程限制型正式卡必须选择限制形态';
      }
    }
    return null;
  };

  const handleCreate = async () => {
    const msg = validateForm();
    if (msg) {
      setError(msg);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await cardPrototypeAPI.create(buildPayload());
      setSuccess('原型卡创建成功');
      setDialogOpen(false);
      await loadData();
    } catch (err: any) {
      setError(err?.message || '创建失败');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editing) return;
    const msg = validateForm();
    if (msg) {
      setError(msg);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await cardPrototypeAPI.update(editing.id, buildPayload());
      setSuccess('原型卡更新成功');
      setEditDialogOpen(false);
      setEditing(null);
      await loadData();
    } catch (err: any) {
      setError(err?.message || '更新失败');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setLoading(true);
    setError(null);
    try {
      await cardPrototypeAPI.remove(deleting.id);
      setSuccess('原型卡删除成功');
      setDeleteDialogOpen(false);
      setDeleting(null);
      await loadData();
    } catch (err: any) {
      setError(err?.message || '删除失败');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (item: CardPrototype, active: boolean) => {
    setLoading(true);
    setError(null);
    try {
      await cardPrototypeAPI.setActive(item.id, active);
      await loadData();
    } catch (err: any) {
      setError(err?.message || '操作失败');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CreditCard sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight="bold">原型卡管理</Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              sx={{ mr: 2 }}
              disabled={loading}
            >
              刷新
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={loading}
            >
              新增原型卡
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>名称</TableCell>
                <TableCell>卡片类型</TableCell>
                <TableCell>性质</TableCell>
                <TableCell>有效天数</TableCell>
                <TableCell>功能类型</TableCell>
                <TableCell>价格</TableCell>
                <TableCell>启用</TableCell>
                <TableCell>排序</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedItems.map((item) => {
                const ft = normalizeFeatureType(item.featureType);
                return (
                  <TableRow key={item.id}>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                    </TableCell>
                    <TableCell>{item.cardType}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.cardNature}
                        size="small"
                        color={item.cardNature === '正式' ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{item.validDays}</TableCell>
                    <TableCell>
                      <Chip
                        label={FEATURE_TYPE_LABEL[ft]}
                        size="small"
                        color={ft === 'COURSE_LIMIT' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{item.price ?? '-'}</TableCell>
                    <TableCell>
                      <Switch
                        checked={!!item.active}
                        disabled={loading}
                        onChange={(e) => handleToggleActive(item, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>{item.sortOrder ?? 0}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => openEdit(item)}
                          disabled={loading}
                          color="primary"
                          title="编辑"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openDelete(item)}
                          disabled={loading}
                          color="error"
                          title="删除"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {sortedItems.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography color="text.secondary">暂无原型卡</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>新增原型卡</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="原型名称 *"
                value={form.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
              <TextField
                fullWidth
                label="卡片类型 *"
                value={form.cardType}
                onChange={(e) => handleInputChange('cardType', e.target.value)}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>性质 *</InputLabel>
                  <Select
                    name="cardNature"
                    value={form.cardNature}
                    label="性质 *"
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="正式">正式</MenuItem>
                    <MenuItem value="试用">试用</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>有效天数 *</InputLabel>
                  <Select
                    name="validDays"
                    value={form.validDays}
                    label="有效天数 *"
                    onChange={handleSelectChange}
                  >
                    {allowedValidDays.map(d => (
                      <MenuItem key={d} value={d}>
                        {d}{d !== 1 && d !== 180 && d !== 365 ? '（不符合规则）' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>功能类型</InputLabel>
                  <Select
                    name="featureType"
                    value={form.featureType}
                    label="功能类型"
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="TIME_LIMIT">{FEATURE_TYPE_LABEL.TIME_LIMIT}</MenuItem>
                    <MenuItem value="COURSE_LIMIT">{FEATURE_TYPE_LABEL.COURSE_LIMIT}</MenuItem>
                  </Select>
                </FormControl>
                {normalizeFeatureType(form.featureType) === 'COURSE_LIMIT' ? (
                  <FormControl fullWidth>
                    <InputLabel>限制形态 *</InputLabel>
                    <Select
                      name="courseLimitScope"
                      value={form.cardNature === '试用' ? 'SINGLE_GRADE' : form.courseLimitScope}
                      label="限制形态 *"
                      onChange={handleSelectChange}
                      disabled={normalizeCardNature(form.cardNature) === '试用'}
                    >
                      <MenuItem value="SINGLE_GRADE">仅1个年级</MenuItem>
                      <MenuItem value="STAGE_ONLY">仅1个阶段</MenuItem>
                    </Select>
                  </FormControl>
                ) : null}
                <TextField
                  fullWidth
                  label="排序"
                  type="number"
                  value={form.sortOrder ?? ''}
                  onChange={(e) => handleInputChange('sortOrder', e.target.value === '' ? '' : Number(e.target.value))}
                />
              </Box>
              <TextField
                fullWidth
                label="价格（可选）"
                type="number"
                value={form.price ?? ''}
                onChange={(e) => handleInputChange('price', e.target.value === '' ? '' : Number(e.target.value))}
              />
              <TextField
                fullWidth
                label="描述（可选）"
                value={form.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2">启用</Typography>
                <Switch
                  checked={form.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>取消</Button>
            <Button onClick={handleCreate} variant="contained" disabled={loading}>创建</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>编辑原型卡</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="原型名称 *"
                value={form.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
              <TextField
                fullWidth
                label="卡片类型 *"
                value={form.cardType}
                onChange={(e) => handleInputChange('cardType', e.target.value)}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>性质 *</InputLabel>
                  <Select
                    name="cardNature"
                    value={form.cardNature}
                    label="性质 *"
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="正式">正式</MenuItem>
                    <MenuItem value="试用">试用</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>有效天数 *</InputLabel>
                  <Select
                    name="validDays"
                    value={form.validDays}
                    label="有效天数 *"
                    onChange={handleSelectChange}
                  >
                    {allowedValidDays.map(d => (
                      <MenuItem key={d} value={d}>
                        {d}{d !== 1 && d !== 180 && d !== 365 ? '（不符合规则）' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>功能类型</InputLabel>
                  <Select
                    name="featureType"
                    value={form.featureType}
                    label="功能类型"
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="TIME_LIMIT">{FEATURE_TYPE_LABEL.TIME_LIMIT}</MenuItem>
                    <MenuItem value="COURSE_LIMIT">{FEATURE_TYPE_LABEL.COURSE_LIMIT}</MenuItem>
                  </Select>
                </FormControl>
                {normalizeFeatureType(form.featureType) === 'COURSE_LIMIT' ? (
                  <FormControl fullWidth>
                    <InputLabel>限制形态 *</InputLabel>
                    <Select
                      name="courseLimitScope"
                      value={form.cardNature === '试用' ? 'SINGLE_GRADE' : form.courseLimitScope}
                      label="限制形态 *"
                      onChange={handleSelectChange}
                      disabled={normalizeCardNature(form.cardNature) === '试用'}
                    >
                      <MenuItem value="SINGLE_GRADE">仅1个年级</MenuItem>
                      <MenuItem value="STAGE_ONLY">仅1个阶段</MenuItem>
                    </Select>
                  </FormControl>
                ) : null}
                <TextField
                  fullWidth
                  label="排序"
                  type="number"
                  value={form.sortOrder ?? ''}
                  onChange={(e) => handleInputChange('sortOrder', e.target.value === '' ? '' : Number(e.target.value))}
                />
              </Box>
              <TextField
                fullWidth
                label="价格（可选）"
                type="number"
                value={form.price ?? ''}
                onChange={(e) => handleInputChange('price', e.target.value === '' ? '' : Number(e.target.value))}
              />
              <TextField
                fullWidth
                label="描述（可选）"
                value={form.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2">启用</Typography>
                <Switch
                  checked={form.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>取消</Button>
            <Button onClick={handleUpdate} variant="contained" disabled={loading}>保存</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>确认删除</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              此操作不可撤销，请谨慎操作
            </Alert>
            <Typography>确定删除原型卡 “{deleting?.name}” 吗？</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
            <Button onClick={handleDelete} color="error" disabled={loading}>删除</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default CardPrototypeManagePage;
